import { Page, Modal, TextField, Select } from '../components/forms';
/**
 * DELIVERY COSTS PAGE
 *
 * Log and manage delivery costs (fuel, salary, vehicle, etc.)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  DatePicker,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface DeliveryCost {
  id: number;
  route_id: number | null;
  route_name: string | null;
  cost_type: string;
  amount: number;
  currency: string;
  date: string;
  notes: string | null;
  receipt_r2_key: string | null;
  created_by: string | null;
  created_at: string;
}

interface CostsResponse {
  costs: DeliveryCost[];
  total: number;
  total_amount: number;
}

export default function DeliveryCosts() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [costs, setCosts] = useState<DeliveryCost[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Form state
  const [costType, setCostType] = useState('fuel');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedDate, setSelectedDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());

  const loadCosts = useCallback(async () => {
    const data = await fetchApi<CostsResponse>('/reports/costs?limit=100');
    if (data) {
      setCosts(data.costs || []);
      setTotalAmount(data.total_amount || 0);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    const result = await fetchApi('/reports/costs', {
      method: 'POST',
      body: JSON.stringify({
        cost_type: costType,
        amount: parseFloat(amount),
        currency: 'EUR',
        date: selectedDay.toISOString().split('T')[0],
        notes: notes || null,
      }),
    });

    if (result) {
      setModalOpen(false);
      setAmount('');
      setNotes('');
      loadCosts();
    }
  };

  const handleDelete = async (id: number) => {
    const result = await fetchApi(`/reports/costs/${id}`, {
      method: 'DELETE',
    });
    if (result) {
      loadCosts();
    }
  };

  const getCostTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fuel: 'Fuel',
      salary: 'Salary',
      vehicle: 'Vehicle',
      tolls: 'Tolls',
      parking: 'Parking',
      maintenance: 'Maintenance',
      insurance: 'Insurance',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getCostTypeBadge = (type: string) => {
    const tones: Record<string, 'success' | 'info' | 'warning' | 'critical' | undefined> = {
      fuel: 'warning',
      salary: 'info',
      vehicle: undefined,
      tolls: undefined,
    };
    return <Badge tone={tones[type]}>{getCostTypeLabel(type)}</Badge>;
  };

  const formatCurrency = (amount: number, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading && costs.length === 0) {
    return (
      <Page title="Delivery Costs">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading costs...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Delivery Costs"
      subtitle="Track fuel, salary, vehicle, and other delivery expenses"
      primaryAction={{
        content: 'Log Cost',
        onAction: () => setModalOpen(true),
      }}
      secondaryActions={[
        {
          content: 'View Report',
          onAction: () => navigate('/reports/delivery-costs'),
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary */}
        <Layout.Section>
          <Card>
            <InlineStack gap="800" wrap>
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Total This Month</Text>
                <Text as="p" variant="headingLg" fontWeight="bold">
                  {formatCurrency(totalAmount)}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Total Entries</Text>
                <Text as="p" variant="headingLg" fontWeight="bold">
                  {costs.length}
                </Text>
              </BlockStack>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Costs Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {costs.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                  headings={['Date', 'Type', 'Amount', 'Route', 'Notes', 'Actions']}
                  rows={costs.map((cost) => [
                    formatDate(cost.date),
                    getCostTypeBadge(cost.cost_type),
                    formatCurrency(cost.amount, cost.currency),
                    cost.route_name || '-',
                    cost.notes || '-',
                    <Button
                      key={`delete-${cost.id}`}
                      size="slim"
                      tone="critical"
                      onClick={() => handleDelete(cost.id)}
                    >
                      Delete
                    </Button>,
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No delivery costs logged"
                  action={{
                    content: 'Log Cost',
                    onAction: () => setModalOpen(true),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Track your delivery expenses to calculate cost per delivery
                    and optimize your operations.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Log Cost Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Log Delivery Cost"
        primaryAction={{
          content: 'Save',
          onAction: handleSave,
          loading,
          disabled: !amount || parseFloat(amount) <= 0,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Select
              label="Cost Type"
              options={[
                { label: 'Fuel', value: 'fuel' },
                { label: 'Salary', value: 'salary' },
                { label: 'Vehicle', value: 'vehicle' },
                { label: 'Tolls', value: 'tolls' },
                { label: 'Parking', value: 'parking' },
                { label: 'Maintenance', value: 'maintenance' },
                { label: 'Insurance', value: 'insurance' },
                { label: 'Other', value: 'other' },
              ]}
              requiredIndicator value={costType}
              onChange={setCostType}
            />

            <TextField
              label="Amount"
              type="number"
              requiredIndicator value={amount}
              onChange={setAmount}
              autoComplete="off"
              prefix="€"
              placeholder="0.00"
            />

            <DatePicker
              month={selectedDate.month}
              year={selectedDate.year}
              onChange={(date) => setSelectedDay(date.start)}
              onMonthChange={(month, year) => setSelectedDate({ month, year })}
              selected={selectedDay}
            />

            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={setNotes}
              autoComplete="off"
              multiline={2}
              placeholder="Receipt number, details, etc."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
